import _ from 'lodash'
import JSONWrapper from './json-wrapper'
import panelSchema from '../../../public/json/panel.schema.json'
import typeSchema from '../../../public/json/types.json'
import { ValidationError } from './error'

/* eslint-disable no-param-reassign */

/**
 * Класс пульта
 *
 * @extends {JSONWrapper}
 */
class Panel extends JSONWrapper {
  constructor(data, elements) {
    super(data, ['itemsById', 'statesBefore', 'elements', 'receivedId'])
    this.setSchemas(panelSchema, [typeSchema])
    this.statesBefore = []
    if (elements) {
      this.elements = elements
      this.items.forEach((item) => {
        const element = this.elements.itemsById[item.itemId]
        item.displayFunctionValues = {
          ...((element && element.displayFunctionValues) || {}),
          ...(item.displayFunctionValues || {})
        }
      })
    }
    this.codeAccessor = (o) => o.code
  }

  afterObjectSet() {
    this.updateItems()
    this.importCode((o) => o.code)
    if (this.receivedId !== null) {
      this.id = this.receivedId
    } else {
      this.receivedId = this.id
    }
  }

  /**
   * Проверка корректности заполнения инструментами
   *
   * @param {Object|null} object
   * @param {Object} params Параметры валидации
   * @param {Resources} Связанные ресурсы
   * @returns {Array.<ValidationError>}
   */
  validateTools(object, params) {
    object = object || this.object
    const usedToolNames = new Set()
    const errors = []
    if (object.tools.length === 0) {
      errors.push(
        new ValidationError('Нужен хотя бы один инструмент', 'warn', '/tools'),
      )
    }
    object.tools.forEach((tool, index) => {
      if (usedToolNames.has(tool.name)) {
        errors.push(
          new ValidationError(
            `Инструмент с именем ${tool.name} уже существует`,
            null,
            `/tools/${index}/name`,
          ),
        )
      } else {
        usedToolNames.add(tool.name)
      }
      const texture = params.resources.texturesById[tool.textureId]
      if (!texture) {
        errors.push(
          new ValidationError(
            `Текстуры {id: ${tool.textureId}} не существует`,
            null,
            `/tools/${index}/textureId`,
          ),
        )
      } else {
        if (!texture.file.physicalName.endsWith('.png')) {
          errors.push(
            new ValidationError(
              'Только текстуры в формате png могут быть использованы для курсора',
              null,
              `/tools/${index}/textureId`,
            ),
          )
        }
        const image = params.resources.imagesById[tool.textureId]
        if (!image) {
          errors.push(
            new ValidationError(
              `Изображение для текстуры {id: ${tool.textureId}} загружается`,
              'info',
              `/tools/${index}/textureId`,
            ),
          )
        } else if (image.width > 32 || image.height > 32) {
          errors.push(
            new ValidationError(
              `Не рекомендуется размер курсора больше 32x32 (у текстуры {id: ${
                tool.textureId
              }} - ${image.width}x${image.height})`,
              'warn',
              `/tools/${index}/textureId`,
            ),
          )
        }
      }
    })
    return errors
  }

  save() {
    const items = this.items.map((item) => {
      const it = _.pick(item, Object.keys(panelSchema.definitions.itemRef.properties))
      return it
    })
    return new Panel(
      {
        id: this.id,
        name: this.name,
        items,
        tools: this.tools,
        background: this.background
      },
      this.elements,
    )
  }

  restore(panel) {
    this.id = panel.id
    this.name = panel.name
    panel.items.forEach((item, index) => {
      if (this.items[index].gif && !item.gif) {
        this.items[index].gif.stop()
      }
      Object.keys(item).forEach((key) => {
        this.items[index][key] = item[key]
      })
    })
    // this.items = panel.items
    this.tools = panel.tools
    this.background = panel.background
    this.itemsById = panel.itemsById
    this.updateItems()
  }

  /**
   * Проверка корректности установки подложки пульта
   *
   * @param {Object|null} object
   * @param {Object} params Параметры валидации
   * @param {Resources} Связанные ресурсы
   * @returns {Array.<ValidationError>}
   */
  validateBackground(object, params) {
    object = object || this.object
    const errors = []
    if (
      object.background !== undefined
      && !params.resources.texturesById[object.background]
    ) {
      errors.push(
        new ValidationError(
          `Текстуры {id: ${object.background}} не существует`,
          null,
          '/background',
        ),
      )
    }
    return errors
  }

  /**
   * Проверка корректности установки элементов пульта
   *
   * @param {Object|null} object
   * @param {Object} params Параметры валидации
   * @param {Object.<number,Object>} params.globalItems Доступные элементы
   * @returns {Array.<ValidationError>}
   */
  validateItems(object, params) {
    object = object || this.object
    const errors = []
    const usedItemIds = new Set()
    object.items.forEach((item, index) => {
      if (usedItemIds.has(item.id)) {
        errors.push(
          new ValidationError(
            `Элемент {id: ${item.id}} уже есть на пульте`,
            null,
            `/items/${index}/id`,
          ),
        )
      } else {
        usedItemIds.add(item.id)
      }
      if (!params.globalItems[item.itemId]) {
        errors.push(
          new ValidationError(
            `Элемента {id: ${item.itemId}} не существует в коллекции`,
            null,
            `/items/${index}/itemId`,
          ),
        )
      }
    })
    return errors
  }

  validateObject(object, params) {
    object = object || this.object
    params = params || {}
    const schemaErrors = super.validateObject(object, params)
    if (schemaErrors.length > 0) {
      return schemaErrors
    }
    return [
      ...this.validateBackground(object, params),
      ...this.validateTools(object, params),
      ...this.validateItems(object, params)
    ]
  }

  sortItems() {
    this.items.sort((item1, item2) => {
      const z1 = item1.zIndex !== undefined ? item1.zIndex : 0
      const z2 = item2.zIndex !== undefined ? item2.zIndex : 0
      return z1 - z2
    })
  }

  /**
   * Доступ к элементам по id. Создает свойство itemsById - объект вида {id: item}
   *
   */
  updateItems() {
    this.sortItems()
    this.itemsById = {}
    this.items.forEach((item) => {
      this.itemsById[item.id] = item
    })
  }

  deleteItem(id) {
    delete this.itemsById[id]
    this.items = this.items.filter((item) => item.id !== id)
  }
}

export default Panel
